import Component from '@bythepixel/component-loader';

const DEFAULTS = {
  activatorSelector: '[data-comp-collapsible-content-activator]',
  activatorTextSelector: '[data-comp-collapsible-content-activator-text]',
  contentContainerSelector: '[data-comp-collapsible-content-container]',
  contentSelector: '[data-comp-collapsible-content-content]',
  optionsSelector: '[data-comp-collapsible-content-options]',
  arrowSelector: '[data-comp-collapsible-content-arrow]',
  collapsedBtnText: 'Show more',
  revealedBtnText: 'Show less',
};

export default class CollapsibleContent extends Component {
  static selector = '[data-comp-collapsible-content]';

  constructor(containerEl, options = DEFAULTS) {
    super(containerEl, options);
    this.activator = this.$container.querySelector(options.activatorSelector);
    this.activatorText = this.activator.querySelector(options.activatorTextSelector);
    this.currentScroll = window.scrollY;
    this.content = this.$container.querySelector(options.contentSelector);
    this.contentContainer = this.$container.querySelector(options.contentContainerSelector);
    this.arrow = this.$container.querySelector(options.arrowSelector);
    this.btnStateText = this.#setBtnStateText();
    this.ariaValuesByState = {
      activator: {
        closed: this.btnStateText.collapsedText,
        revealed: this.btnStateText.revealedText,
      },
    };
    this.mount();
  }

  #setBtnStateText() {
    const collapsedText = this.$container.dataset.collapsedButtonText || this.$options.collapsedBtnText;
    const revealedText = this.$container.dataset.revealedButtonText || this.$options.revealedBtnText;

    return {
      collapsedText,
      revealedText,
    };
  }

  #setActivatorText(text) {
    this.activatorText.textContent = text;
  }

  get #contentShowing() {
    return this.$container.dataset.compCollapsibleContentContentShowing === 'true';
  }

  set #contentShowing(updatedState) {
    this.$container.dataset.compCollapsibleContentContentShowing = updatedState;
  }

  get #activatorTriggered() {
    return this.activator.dataset.isTriggered === 'true';
  }

  set #activatorTriggered(updatedState) {
    this.activator.dataset.isTriggered = updatedState;
  }

  #updateAriaTags(contentShowing = false) {
    const state = contentShowing ? 'revealed' : 'closed';
    const ariaHidden = contentShowing ? 'false' : 'true';

    this.activator.setAttribute('aria-label', this.ariaValuesByState.activator[state]);
    this.content.setAttribute('aria-hidden', ariaHidden);
  }

  #followCollapse(scrollTo = 0) {
    window.scrollTo({
      top: scrollTo,
      behavior: 'smooth',
    });
  }

  #handleClick = () => {
    const { content, contentContainer } = this;
    const contentHeight = content.getBoundingClientRect().height;
    let isCollapsing = false;

    this.#contentShowing = this.#contentShowing ? 'false' : 'true';

    if (!this.#contentShowing) {
      isCollapsing = true;
      this.#followCollapse(this.currentScroll);
    }

    // toggle classes
    contentContainer.classList.toggle('active');
    this.arrow.classList.toggle('active');

    // update the max-height to trigger the open/close animation
    if (!isCollapsing) {
      this.setContentContainerMaxHeight(contentHeight);
    } else {
      this.setContentContainerMaxHeight(0);
    }

    // update the activator state
    this.#activatorTriggered = (!this.#activatorTriggered).toString();

    // if we're not collapsing, record the user's current scroll position
    if (!isCollapsing) {
      this.currentScroll = window.scrollY;
    }

    const updatedActivatorText = this.#contentShowing
      ? this.btnStateText.revealedText
      : this.btnStateText.collapsedText;

    // update text and aria attributes
    this.#setActivatorText(updatedActivatorText);
    this.#updateAriaTags(this.#contentShowing);
  }

  #attachListeners() {
    this.activator.addEventListener('click', this.#handleClick);
  }

  setContentContainerMaxHeight(containerHeight) {
    const heightString = containerHeight ? `${containerHeight}px` : '0';

    this.contentContainer.style.maxHeight = heightString;
  }

  setContentContainerTransition(containerHeight = 0) {
    // somewhat variable max-height transition based off the container's height. a faster animation
    // is a little jarring on a taller container
    const speed = Math.round(containerHeight) > 750 ? '1s' : '550ms';

    this.contentContainer.style.transition = `
      max-height ${speed} ease-in-out,
      opacity 750ms ease
    `;
  }

  mount() {
    const { content } = this;

    // go ahead and update the max-height transition speed
    this.setContentContainerTransition(content.getBoundingClientRect().height);

    this.#attachListeners();
  }
}
